import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button, Loading } from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import ProgrammeRecommendationDeletion from './ProgrammeRecommendationDeletion';
import ProgrammeRecommendationForm from './ProgrammeRecommendationForm';
import ProgrammeRecommendationsTable from './ProgrammeRecommendationsTable';

import { ProgrammeRecommendation } from '../../types';
import { RecommendationCopyKeys, createRecommendationCopy } from '../../copy';

import { getProgrammeRecommendations } from '@api';
import { CurriculumQueryKeys } from '@services';
import { ErrorActionPanel, PageLayout, TabsPanel } from '@shared';
import { Country } from '@types';
import { countryTabList } from '@utils';

const ProgrammesRecommendationsPage = () => {
    const [selectedTab, setSelectedTab] = useState<Country>(Country.GB);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<ProgrammeRecommendation>();
    const [deleting, setDeleting] = useState<ProgrammeRecommendation>();

    const { status, data: recommendations } = useQuery<
        ProgrammeRecommendation[]
    >({
        queryKey: [CurriculumQueryKeys.PROGRAMME_RECOMMENDATIONS, selectedTab],
        queryFn: () => getProgrammeRecommendations(selectedTab),
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const handleCreate = () => {
        setEditing(undefined);
        setShowForm(true);
    };

    const handleEdit = (recommendation: ProgrammeRecommendation) => {
        setEditing(recommendation);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditing(undefined);
    };

    return (
        <PageLayout
            title="Programme Recommendations"
            buttons={[
                <Button
                    key="create-recommendation"
                    variant="contained"
                    color="primary"
                    onClick={handleCreate}
                    data-testid="create-recommendation-button"
                >
                    {createRecommendationCopy(
                        RecommendationCopyKeys.CREATE_TITLE,
                    )}
                </Button>,
            ]}
        >
            <TabsPanel<Country>
                selectedTab={selectedTab}
                tabList={countryTabList}
                setSelectedTab={setSelectedTab}
            />

            {status === QUERY_STATUS.pending && (
                <Loading loadingMessage="Loading recommendations..." />
            )}

            {status === QUERY_STATUS.error && (
                <ErrorActionPanel
                    error="There was an error loading the recommendations"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {status === QUERY_STATUS.success && recommendations && (
                <>
                    <ProgrammeRecommendationsTable
                        recommendations={recommendations}
                        country={selectedTab}
                        onEdit={handleEdit}
                        onDelete={setDeleting}
                    />

                    {showForm && (
                        <ProgrammeRecommendationForm
                            key={editing?.id ?? 'new'}
                            open={showForm}
                            onClose={handleCloseForm}
                            country={selectedTab}
                            recommendation={editing}
                        />
                    )}

                    {deleting && (
                        <ProgrammeRecommendationDeletion
                            open={Boolean(deleting)}
                            recommendationId={deleting.id}
                            setShowDelete={() => setDeleting(undefined)}
                        />
                    )}
                </>
            )}
        </PageLayout>
    );
};

export default ProgrammesRecommendationsPage;
